import { useEffect } from 'react';

/** How far the user has to pull, in pixels, before the header comes back. */
const THRESHOLD = 140;

interface PullToRevealOptions {
  /** Watch for the gesture at all — off while the header is already showing. */
  enabled: boolean;
  /**
   * Whether the thing being scrolled is at its top. The gesture only counts
   * from there: mid-list, scrolling up is just scrolling up.
   */
  isAtTop: () => boolean;
  onReveal: () => void;
}

/**
 * Brings a collapsed header back when the user scrolls or drags upwards **with
 * intent** from the top.
 *
 * Deliberately not a plain "scrolled up by one notch": the header folds away so
 * the climbs can be read, and a list that springs its header back on the first
 * flick of a finger cannot be read at all. The distance is accumulated and only
 * a sustained pull past `THRESHOLD` counts, which is the same gesture as a
 * pull-to-refresh and reads as one.
 *
 * Listeners sit on the window rather than on the scroller: wheel and touch
 * events bubble there anyway, and `isAtTop` is what decides whether the gesture
 * belongs to this scroller — which lets the same hook serve a page that scrolls
 * the document and a panel that scrolls inside itself.
 */
export function usePullToReveal({ enabled, isAtTop, onReveal }: PullToRevealOptions) {
  useEffect(() => {
    if (!enabled) return;

    let wheelAcc = 0;
    let lastWheel = 0;
    let touchStartY = 0;
    let pulling = false;

    const onWheel = (e: WheelEvent) => {
      if (!isAtTop() || e.deltaY >= 0) { wheelAcc = 0; return; }
      const now = Date.now();
      // A pause breaks the gesture: two unrelated flicks are not one pull.
      if (now - lastWheel > 300) wheelAcc = 0;
      lastWheel = now;
      wheelAcc += -e.deltaY;
      if (wheelAcc >= THRESHOLD) { wheelAcc = 0; onReveal(); }
    };
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
      pulling = isAtTop();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!pulling || !isAtTop()) return;
      const dy = (e.touches[0]?.clientY ?? 0) - touchStartY;
      if (dy >= THRESHOLD) { pulling = false; onReveal(); }
    };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [enabled, isAtTop, onReveal]);
}
